import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, Button, Grid, Stack } from '@mui/material';
import { auditApi, RouteEnum } from 'App';
import {
  AnomaliesSidebar,
  AuditStatuses,
  duration,
  EnhancedTimeline,
  Loader,
  ReviewIdentityDialog,
  Screenshots,
  StatusesMenu,
  usePartner,
  usePreSignedUrl,
  Video,
} from '@shared';

import useEnhancedAudit from './EnhancedAudit.hook';
import { FinishAuditDialog } from './components';
import * as S from './EnhancedAudit.styled';

export function EnhancedAudit() {
  const {
    isLeadAuditor,
    isInstructor,

    isStatusesLoading,
    isCommentsLoading,
    isOpenReviewIdentity,
    isOpenFinishAudit,
    isExamAuditorApproved,
    isNotificationShowing,
    isPlaying,
    isSuccessTargetRegions,

    playedSeconds,
    playbackRate,
    progressPlayed,
    playerRef,

    auditUuid,
    anomalies,
    targetRegions,
    statuses,
    comments,

    handleNotApproveExamTaker,
    handleSeekChange,
    setIsOpenReviewIdentity,
    setIsOpenFinishAudit,
    setNotificationShowing,
    setIsExamAuditorApproved,
    setIsPlaying,
    setProgressPlayed,
    setPlaybackRate,
    setPlayedSeconds,
    updateStatuses,
    refetchAnomalies,
    addComment,
  } = useEnhancedAudit();

  const { data, isError, isLoading } = useQuery(
    ['exam session', auditUuid],
    () => auditApi.getSessionReview(auditUuid),
    {
      select: (session) => ({
        ...session,
        stream: {
          traces: session.stream.traces,
          streamHandle: session.stream.handle,
          videoStartClock: session.stream.videoStartClock,
          videoEndClock: session.stream.videoEndClock,
        },
      }),
    }
  );

  const { preSignedUrl, refreshPreSignedUrl } = usePreSignedUrl(data?.stream.traces[0].handle);

  const { showDocumentToPartner } = usePartner({ partner: data?.partnerUuid });

  const videoDurationStreamService = data
    ? duration(data.stream.videoStartClock, data.stream.videoEndClock)
    : 0;

  const videoDuration =
    (playerRef.current && playerRef.current.getDuration() * 1000) || videoDurationStreamService;

  const allowedProgressRegions = useMemo(
    () =>
      targetRegions.map((region) => {
        return {
          start: region.start / videoDuration,
          end: region.end / videoDuration,
        };
      }),
    [targetRegions, videoDuration]
  );

  const roomScanTraceHandle =
    isLeadAuditor || isInstructor ? data?.roomScanTrace?.handle : undefined;

  if (isLoading) {
    return <Loader />;
  }
  if (isError) {
    return null;
  }

  return (
    <Box component='article' sx={{ pr: '312px', width: '100%', height: '100%' }}>
      <S.TitleWrapper>
        <AuditStatuses
          auditUuid={auditUuid}
          partnerName={data?.partnerName}
          statuses={statuses}
          isExamAuditorApproved={isExamAuditorApproved}
          isSpecialPartner={data?.specialPartner}
        />
        <Stack direction='row' gap={1} alignItems='center'>
          <Link to={RouteEnum.AuditQueue} style={{ textDecoration: 'none' }}>
            <Button variant='outlined'>Back to Queue</Button>
          </Link>
          <Button
            disabled={isNotificationShowing}
            variant='contained'
            onClick={() => setIsOpenFinishAudit(true)}
          >
            Finish Audit
          </Button>
          <StatusesMenu
            useStatuses={{ statuses, updateStatuses, isStatusesLoading }}
            openReviewIdentity={() => setIsOpenReviewIdentity(true)}
            isNotificationShowing={isNotificationShowing}
            auditUuid={auditUuid}
          />
        </Stack>
      </S.TitleWrapper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Video
            src={preSignedUrl}
            playing={isPlaying}
            setPlaying={setIsPlaying}
            onSetProgressPlayed={setProgressPlayed}
            playerRef={playerRef}
            playbackRate={playbackRate}
            onSetPlaybackRate={setPlaybackRate}
            onSetPlayedSeconds={setPlayedSeconds}
            playedSeconds={playedSeconds}
            regions={targetRegions}
            progressPlayed={progressPlayed}
            onSeekChange={handleSeekChange}
            videoDuration={videoDuration}
            isSuccessTargetRegions={isSuccessTargetRegions}
            allowedProgressRegions={allowedProgressRegions}
            refreshPreSignedUrl={refreshPreSignedUrl}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Screenshots
            screenshotsClock={data.screenshotsClock}
            stream={data.stream}
            progressPlayed={progressPlayed}
          />
        </Grid>
      </Grid>
      <EnhancedTimeline
        allowedProgressRegions={allowedProgressRegions}
        progressPlayed={progressPlayed}
        videoDuration={videoDuration}
        onSeekChange={handleSeekChange}
        targetRegions={targetRegions}
        anomalies={anomalies}
        isPlaying={isPlaying}
      />
      <AnomaliesSidebar
        auditUuid={auditUuid}
        playedSeconds={playedSeconds}
        onSeekChange={handleSeekChange}
        anomalies={anomalies}
        refetchAnomalies={refetchAnomalies}
        setPlaying={setIsPlaying}
      />
      <ReviewIdentityDialog
        title='Is this the exam taker?'
        auditUuid={auditUuid}
        isModalOpen={isOpenReviewIdentity}
        onCloseModal={() => setIsOpenReviewIdentity(false)}
        profileTraceHandle={data.profileTrace.handle}
        documentTraceHandle={data.documentTrace?.handle}
        roomScanTraceHandle={roomScanTraceHandle}
        showDocumentToPartner={showDocumentToPartner}
        refetchAnomalies={refetchAnomalies}
        identityVerification
        onApproveExamTaker={() => setIsExamAuditorApproved(true)}
        onNotApproveExamTaker={handleNotApproveExamTaker}
        hideNotificationShowing={() => setNotificationShowing(false)}
      />
      <FinishAuditDialog
        isModalOpen={isOpenFinishAudit}
        onCloseModal={() => setIsOpenFinishAudit(false)}
        useStatuses={{ statuses, updateStatuses, isStatusesLoading }}
        useComments={[comments, addComment, isCommentsLoading]}
        auditUuid={auditUuid}
      />
    </Box>
  );
}
